import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
} from '@angular/core'
import {FormBuilder, FormGroup, Validators} from '@angular/forms'
import {Observable, BehaviorSubject, Subject} from 'rxjs'
import {finalize, tap, flatMap, takeUntil} from 'rxjs/operators'
import {Contract, ContractType} from '@app/core/services/rest-api.model'
import {DomainDataManagerService} from '@app/core/services/domain/domain-data-manager.service'
import {ContextService} from '@app/core/services/context.service'
import {ContractManagerService} from '@app/core/services/contract/contract-manager.service'
import {
  ContractService,
  CreateDomainRequestData,
} from '@app/core/services/rest-api'
import {FormBasedComponent} from '@app/features/base/form-based.component'
import {BsModalRef} from 'ngx-bootstrap/modal'
import {NewUserComponent} from '@app/features/administration/users/new-user/new-user.component'

@Component({
  templateUrl: './domain-selector-new-modal.component.html',
  providers: [DomainDataManagerService, ContractManagerService],
  standalone: false,
})
export class DomainSelectorNewModalComponent
  extends FormBasedComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild(NewUserComponent) newUserComponent: NewUserComponent
  constructor(
    protected formBuilder: FormBuilder,
    private managerSvc: DomainDataManagerService,
    private contractSvc: ContractService,
    private contextSvc: ContextService,
    public bsModalRef: BsModalRef,
  ) {
    super(formBuilder)
  }

  loading$: BehaviorSubject<boolean> = new BehaviorSubject(false)
  tmsContractType: ContractType
  initialCode: string
  onComplete
  destroy$ = new Subject<void>()
  createUser = false

  ngOnInit() {
    super.initForm()
    this.loading$.next(true)

    // load the available contract types
    let sub = this.contractSvc
      .getContractTypes(this.contextSvc.activeDomain)
      .pipe(
        tap((contractTypes) => {
          this.tmsContractType = contractTypes.find(
            (elem) => elem.code == 'tms',
          )
          // disable option if tms contract type not available for user
          if (!this.tmsContractType) {
            return this.form.get('createTmsContract').disable()
          }
        }),
        finalize(() => {
          this.loading$.next(false)
          sub.unsubscribe()
        }),
      )
      .subscribe()
  }

  ngAfterViewInit(): void {
    this.form
      .get('parent')
      .valueChanges.pipe(
        tap((domain) => {
          this.newUserComponent.form.get('domain').setValue(domain)
        }),
        takeUntil(this.destroy$),
      )
      .subscribe()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  buildForm(fb: FormBuilder): FormGroup {
    return fb.group(
      {
        parent: fb.control(this.contextSvc.activeDomain, Validators.required),
        code: fb.control(this.initialCode, [
          Validators.required,
          Validators.pattern('[a-zA-Z0-9_\\-.]+'),
        ]),
        name: fb.control(null, Validators.required),
        externalId: fb.control(null),
        createTmsContract: fb.control(false, Validators.required),
        createUser: fb.control(false, null),
      },
      {
        //asyncValidator: this.uniqueCodeValidator.validator()
      },
    )
  }

  get parent() {
    return this.form.get('parent')
  }
  get name() {
    return this.form.get('name')
  }
  get code() {
    return this.form.get('code')
  }
  get externalId() {
    return this.form.get('externalId')
  }
  get createTmsContract() {
    return this.form.get('createTmsContract')
  }

  get defaults() {
    return {
      code: this.initialCode,
      parent: this.contextSvc.activeDomain,
      createTmsContract: false,
    }
  }

  closeModal() {
    this.bsModalRef.hide()
  }

  processSubmit(form: FormGroup): Observable<any> {
    const requestData: CreateDomainRequestData = {
      domain: {
        code: this.trim(form.get('code').value),
        name: this.trim(form.get('name').value),
        externalId: form.get('externalId').value,
        parent: form.get('parent').value,
      },
    }
    if (form.get('createUser').value) {
      const userFormData = this.newUserComponent.form.value
      const user = {
        username: userFormData.username,
        emailAddress: userFormData.emailAddress,
        roleIds: userFormData.roles.map((item) => item.id),
      }
      requestData.user = user
      requestData.sendPasswordEmail = true
      requestData.theme = userFormData.myPayterBeta ? 'mypayter' : null
    }

    return this.managerSvc.create(requestData).pipe(
      tap(() => {
        this.contextSvc.refresh()
      }),
      flatMap((created) => {
        if (form.get('createTmsContract').value) {
          const contract: Contract = {
            name: created.code + ' - tms',
            type: this.tmsContractType,
            domain: created.code,
          }
          return this.contractSvc.postContract(created.code, contract).pipe(
            tap(() => {
              this.onComplete(created.code)
              this.bsModalRef.hide()
            }),
          )
        } else {
          this.onComplete(created.code)
          this.bsModalRef.hide()
        }
      }),
    )
  }
}
